import React from "react";
import { useTranslation } from "react-i18next";
import PhoneInput from "react-phone-number-input";
import { RedeemStepHeading } from "./RedeemStepHeading";

type Props = {
  formState: {
    points: number;
    email: string;
    mobile: string;
    requestType: string;
    errors: { points: string; email: string; mobile: string };
  };
  pointDisplay: any;
  isBonusRoute: boolean;
  countryCode: any;
  isOtpConsentChecked: boolean;
  onConsentChange: () => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onMobileChange: (value: any) => void;
};

/** Step 2 of the redemption checkout: points, contact details and SMS consent. Presentation only. */
export const RedeemDetailsStep = ({
  formState,
  pointDisplay,
  isBonusRoute,
  countryCode,
  isOtpConsentChecked,
  onConsentChange,
  onChange,
  onMobileChange,
}: Props) => {
  const { t } = useTranslation();

  return (
    <section className="zs-card">
      <div className="zs-card-header">
        <RedeemStepHeading
          step={2}
          title="Confirm your details"
          subtitle="We send one-time verification codes to this email and phone number."
        />
      </div>

      <div className="flex flex-col gap-4 zs-card-body">
        <div className="from-group">
          <label className="zs-label zs-label-required" htmlFor="points" aria-label="points">
            {t("page.referral.Points1")}
          </label>
          {formState.requestType === "Study" || formState.requestType === "Bonus" ? (
            <div>
              <input
                type="number"
                min="0"
                className={`zs-input tabular-nums ${isBonusRoute ? "disabled:text-ink-3" : ""} appearance-none`}
                id="points"
                name="points"
                disabled
                aria-describedby=""
                value={
                  (formState.requestType === "Study" || formState.requestType === "Bonus") && +pointDisplay
                    ? +pointDisplay
                    : ""
                }
                onChange={onChange}
                onWheel={(e) => e.preventDefault()}
              />
              <span id="points_error" className="text-danger"></span>
            </div>
          ) : (
            <div>
              <input
                type="number"
                min="0"
                className="zs-input tabular-nums appearance-none"
                id="points"
                name="points"
                aria-describedby=""
                aria-invalid={formState.errors.points ? "true" : undefined}
                value={formState.points}
                onChange={onChange}
                onWheel={(e) => e.preventDefault()}
              />
              <span id="points_error" className="text-danger"></span>
            </div>
          )}

          {formState.errors.points ? (
            <p className="zs-error" id="emai_err_msg">
              {formState.errors.points}
            </p>
          ) : null}
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="form-group">
            <label className="zs-label zs-label-required" htmlFor="email" aria-label="email">
              {t("page.referral.Email")}
            </label>
            <div>
              <input
                type="email"
                className="zs-input bg-surface-2"
                id="email"
                name="email"
                value={formState.email}
                readOnly={true}
              />
            </div>
            {formState.errors.email ? (
              <p className="zs-error" id="emai_err_msg">
                {formState.errors.email}
              </p>
            ) : null}
          </div>

          <div className="form-group">
            <label className="zs-label zs-label-required">{t("page.referral.Mobile")}</label>
            <PhoneInput
              className="flex items-center gap-2 zs-input focus-within:border-primary"
              international
              countryCallingCodeEditable={false}
              defaultCountry={countryCode}
              value={formState.mobile}
              onChange={onMobileChange}
              countries={["US", "CA", "IN"]}
            />
            {formState.errors.mobile ? (
              <p className="zs-error" id="emai_err_msg">
                {formState.errors.mobile}
              </p>
            ) : null}
          </div>
        </div>

        <div className="flex items-start gap-3 p-3 border rounded-lg border-line bg-surface-2">
          <input
            type="checkbox"
            id="otpConsent"
            checked={isOtpConsentChecked}
            onChange={onConsentChange}
            className="mt-0.5 w-4 h-4 cursor-pointer accent-primary shrink-0"
          />
          <label htmlFor="otpConsent" className="text-xs leading-relaxed text-ink-2">
            I agree to receive Transactional SMS messages with one-time passwords (OTP) for identity verification
            during reward redemption from Zamplia Survey. Message frequency varies. Message & data rates may apply.
            Reply STOP to opt out at any time.{" "}
            <a
              href="https://zampliasurveys.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold underline zs-link"
              onClick={(e) => e.stopPropagation()}
            >
              Privacy Policy
            </a>
          </label>
        </div>
      </div>
    </section>
  );
};
